import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { DocumentGroupsTable } from '../../../../components/documents/DocumentGroupsTable'
import { UploadDocumentButton } from '../../../../components/documents/UploadDocumentButton'
import { StatusPill } from '../../../../components/StatusPill'
import { useOrgDocumentGroups } from '../../../../models/document'
import { EsignEnvelopeStatus, useOrgEnvelopes } from '../../../../models/esign'
import { EnvelopeSignLinkButton } from './EnvelopeSignLinkButton'
import { SendAuthorizationLinkMenu } from './SendAuthorizationLinkMenu'
import { SweepDocumentsButton } from './SweepDocumentsButton'

function formatDate(dateStr: string | null) {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function DocumentsTab({ orgId }: { orgId: string }) {
  const { groups, isLoading: documentsLoading } = useOrgDocumentGroups(orgId)
  const { envelopes, isLoading: envelopesLoading } = useOrgEnvelopes(orgId)

  return (
    <div className="flex flex-col gap-5">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <div className="flex flex-col gap-1.5">
            <CardTitle>Documents</CardTitle>
            <CardDescription>Uploaded documents for this organization.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <SweepDocumentsButton orgId={orgId} />
            <UploadDocumentButton orgId={orgId} />
          </div>
        </CardHeader>
        <CardContent>
          {documentsLoading ? <SkeletonRows /> : <DocumentGroupsTable groups={groups} orgId={orgId} />}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>Envelopes</CardTitle>
          <SendAuthorizationLinkMenu orgId={orgId} />
        </CardHeader>
        <CardContent>
          {envelopesLoading ? (
            <SkeletonRows rows={3} />
          ) : envelopes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">Recipient</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold">Sent</TableHead>
                  <TableHead className="font-semibold">Signed</TableHead>
                  <TableHead className="text-right font-semibold">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {envelopes.map((env) => (
                  <TableRow key={env.id}>
                    <TableCell className="font-medium">{env.type || '-'}</TableCell>
                    <TableCell className="font-medium">{env.recipientName || '-'}</TableCell>
                    <TableCell>{env.recipientEmail}</TableCell>
                    <TableCell>
                      <StatusPill status={env.status} />
                    </TableCell>
                    <TableCell>{formatDate(env.dateSent)}</TableCell>
                    <TableCell>{formatDate(env.dateSigned)}</TableCell>
                    <TableCell className="text-right">
                      {env.status === EsignEnvelopeStatus.SENT && (
                        <EnvelopeSignLinkButton orgId={orgId} envelopeId={env.id} />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No envelopes found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
